import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, Loader, ChevronUp, ChevronDown, Edit2, X } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import GatewayCredentialFields, { SECTION, FIELD, blankGatewayProfile } from './GatewayFields';

// Settings → Payments → Routing (2026-07-28). Two things live here:
//
//   Profiles — one per merchant account (credentials + surcharge rates).
//   Rules    — an ORDERED list; the first one matching a payment picks its profile.
//
// Order is the semantics, so rules save as one whole array rather than individually; the
// server re-validates the ordering constraints in routes/settings.js either way.

const notify = (msg, kind) => (kind === 'error' ? toast.error(msg) : toast.success(msg));

const emptyUi = { showSecret: false, showWebhookSecret: false, showAblepaySalt: false };

const blankProfile = blankGatewayProfile;

const SERVICE_LABEL = { any: 'Any service', catv: 'CableTV', broadband: 'Broadband' };
const PURPOSE_LABEL = { any: 'Any', renewal: 'Renewal', balance: 'Balance' };

// Human-readable amount window — the two bounds are inclusive, which is easy to get wrong
// when reading a bare min/max pair, so spell it out.
function amountLabel(r) {
  const has = v => v !== null && v !== undefined && v !== '';
  if (!has(r.minAmount) && !has(r.maxAmount)) return 'Any amount';
  if (!has(r.minAmount)) return `≤ ₹${Number(r.maxAmount).toLocaleString('en-IN')}`;
  if (!has(r.maxAmount)) return `≥ ₹${Number(r.minAmount).toLocaleString('en-IN')}`;
  return `₹${Number(r.minAmount).toLocaleString('en-IN')} – ₹${Number(r.maxAmount).toLocaleString('en-IN')}`;
}

export default function GatewayRoutingTab() {
  const [profiles, setProfiles] = useState([]);
  const [rules, setRules] = useState([]);
  // Per-service gateway choice, set on the Online PG tabs — read-only here, but it decides
  // whether the rules below are consulted at all for a given service.
  const [serviceRouting, setServiceRouting] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingRules, setSavingRules] = useState(false);

  const [editing, setEditing] = useState(null);      // profile being edited/created
  const [editUi, setEditUi] = useState(emptyUi);
  const [savingProfile, setSavingProfile] = useState(false);

  const [preview, setPreview] = useState({ service: 'broadband', amount: 500, purpose: 'balance', result: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings/payment-gateway-profiles');
      setProfiles(data.data.profiles || []);
      setRules(data.data.rules || []);
      setServiceRouting(data.data.serviceRouting || {});
    } catch (err) {
      notify(err.response?.data?.message || 'Could not load gateway profiles', 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setEditField = (key, val) => setEditing(p => ({ ...p, [key]: val }));
  const setEditFee = (method, field, val) => setEditing(p => ({
    ...p, feesByMethod: { ...p.feesByMethod, [method]: { ...p.feesByMethod[method], [field]: val } },
  }));

  const startEdit = (profile) => {
    // Secrets come back from the API already masked; the bullets mean "unchanged" and the
    // server drops them on save, so they can be handed straight to the form.
    setEditing({ ...blankProfile(), ...profile });
    setEditUi(emptyUi);
  };

  const saveProfile = async () => {
    if (!editing.name?.trim()) return notify('Profile name is required', 'error');
    setSavingProfile(true);
    try {
      if (editing._id) await api.put(`/settings/payment-gateway-profiles/${editing._id}`, editing);
      else await api.post('/settings/payment-gateway-profiles', editing);
      notify('Profile saved', 'success');
      setEditing(null);
      await load();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not save profile', 'error');
    } finally { setSavingProfile(false); }
  };

  const deleteProfile = async (profile) => {
    if (!window.confirm(`Delete gateway profile "${profile.name}"? Payments already made through it are unaffected.`)) return;
    try {
      await api.delete(`/settings/payment-gateway-profiles/${profile._id}`);
      notify('Profile deleted', 'success');
      await load();
    } catch (err) {
      // 409 here means routing rules still point at it — the message names the count.
      notify(err.response?.data?.message || 'Could not delete profile', 'error');
    }
  };

  const setRule = (i, key, val) => setRules(rs => rs.map((r, j) => j === i ? { ...r, [key]: val } : r));
  const moveRule = (i, delta) => setRules(rs => {
    const next = [...rs];
    const j = i + delta;
    if (j < 0 || j >= next.length) return rs;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const addRule = () => setRules(rs => [...rs, {
    service: 'any', purpose: 'any', minAmount: null, maxAmount: null,
    profile: profiles[0]?._id || '', isEnabled: true,
  }]);

  const saveRules = async () => {
    setSavingRules(true);
    try {
      // Blank number inputs come through as '' — normalise to null so "unbounded" survives
      // the round-trip instead of being coerced to 0, which would mean something quite
      // different (a minAmount of 0 still matches everything, but a maxAmount of 0 matches
      // nothing at all).
      const clean = rules.map(r => ({
        ...r,
        minAmount: r.minAmount === '' || r.minAmount === undefined ? null : r.minAmount,
        maxAmount: r.maxAmount === '' || r.maxAmount === undefined ? null : r.maxAmount,
      }));
      const { data } = await api.put('/settings/payment-gateway-rules', { rules: clean });
      setRules(data.data || clean);
      notify('Routing rules saved', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Could not save routing rules', 'error');
    } finally { setSavingRules(false); }
  };

  const runPreview = async () => {
    try {
      const { data } = await api.post('/settings/payment-gateway-profiles/preview', {
        service: preview.service, amount: Number(preview.amount) || 0, purpose: preview.purpose,
      });
      setPreview(p => ({ ...p, result: data.data }));
    } catch (err) {
      notify(err.response?.data?.message || 'Preview failed', 'error');
    }
  };

  if (loading) return <div className="card"><div className="card-body"><Loader size={16} className="spin" /> Loading…</div></div>;

  // Services whose Online PG tab pins them to one gateway, so the rules never run for them.
  const bypassed = ['catv', 'broadband'].reduce((acc, service) => {
    const r = serviceRouting[service];
    if (r?.mode === 'profile') {
      const name = profiles.find(p => String(p._id) === String(r.profile))?.name;
      acc.push({ service, label: name ? `“${name}” only` : 'a single profile' });
    } else if (r?.mode === 'split') {
      acc.push({ service, label: `two gateways split at ₹${Number(r.splitThreshold || 0).toLocaleString('en-IN')}` });
    } else if (r?.mode === 'custom') {
      acc.push({ service, label: 'its own custom credentials' });
    }
    return acc;
  }, []);

  return (
    <>
      {/* ── Profiles ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Gateway Profiles</h3>
              <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                One profile per merchant account. Which one takes a given payment is decided by the routing rules below.
              </p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => startEdit(blankProfile())}>
              <Plus size={13} style={{ marginRight: 5 }} />Add Profile
            </button>
          </div>

          {!profiles.length && (
            <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginTop: 16 }}>
              No profiles yet — payments are still routed by the legacy per-service CableTV / Broadband tabs.
              Run <code>node migrate-gateway-profiles.js</code> to import those two as profiles, or add one here.
            </p>
          )}

          {profiles.map(p => (
            <div key={p._id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '.92rem' }}>{p.name}</div>
                <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                  {p.provider}{p.testMode ? ' · test mode' : ''}
                  {p.provider === 'ablepay' && p.ablepayPgApiUrl ? ` · ${p.ablepayPgApiUrl}` : ''}
                  {p.passFeeToCustomer ? ' · fee charged to customer' : ''}
                </div>
              </div>
              <span className={`badge ${p.isEnabled ? 'badge-success' : 'badge-secondary'}`}>
                {p.isEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <button className="btn btn-secondary btn-sm" onClick={() => startEdit(p)}><Edit2 size={12} /></button>
              <button className="btn btn-danger btn-sm" onClick={() => deleteProfile(p)}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Profile editor ── */}
      {editing && (
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--primary)' }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>{editing._id ? `Edit “${editing.name}”` : 'New Gateway Profile'}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}><X size={13} /></button>
            </div>

            <SECTION title="Profile">
              <FIELD label="Name" required hint="Shown in the routing rules below — e.g. “AblePay Retail” or “AblePay Main”">
                <input className="form-control" style={{ maxWidth: 400 }} value={editing.name}
                  onChange={e => setEditField('name', e.target.value)} placeholder="e.g. AblePay Retail" />
              </FIELD>
            </SECTION>

            <GatewayCredentialFields
              pg={editing} ui={editUi}
              setField={setEditField}
              setUiField={(k, v) => setEditUi(u => ({ ...u, [k]: v }))}
              setFeeMethod={setEditFee}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveProfile} disabled={savingProfile}>
                <Save size={14} style={{ marginRight: 6 }} />{savingProfile ? 'Saving…' : 'Save Profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rules ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Routing Rules</h3>
              <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', margin: '4px 0 0', maxWidth: 640 }}>
                Checked top to bottom — the <strong>first matching rule wins</strong>, so put specific rules above
                general ones and keep an “any amount” catch-all last. Amount bounds are inclusive and match the
                base amount, before any customer surcharge.
              </p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={addRule} disabled={!profiles.length}>
              <Plus size={13} style={{ marginRight: 5 }} />Add Rule
            </button>
          </div>

          {/* A service pinned on its own Online PG tab never reaches these rules, which is
              invisible from here otherwise — easy to spend a while editing rules that
              nothing evaluates. */}
          {bypassed.length > 0 && (
            <p style={{ fontSize: '.83rem', color: 'var(--warning, #b45309)', margin: '12px 0 0' }}>
              These rules do not apply to {bypassed.map(b => SERVICE_LABEL[b.service]).join(' or ')} —
              {bypassed.map(b => ` ${SERVICE_LABEL[b.service]} pays through ${b.label}`).join(', ')}.
              Change that on the {bypassed.length > 1 ? 'Online PG tabs' : `${SERVICE_LABEL[bypassed[0].service]} — Online PG tab`}.
            </p>
          )}

          {!rules.length && (
            <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginTop: 16 }}>
              No rules — every payment falls back to the legacy per-service gateway config.
            </p>
          )}

          {rules.map((r, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap',
              padding: '12px 0', borderBottom: '1px solid var(--border)',
              opacity: r.isEnabled === false ? 0.5 : 1,
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button className="btn btn-secondary btn-sm" style={{ padding: '1px 5px' }}
                  onClick={() => moveRule(i, -1)} disabled={i === 0}><ChevronUp size={11} /></button>
                <button className="btn btn-secondary btn-sm" style={{ padding: '1px 5px' }}
                  onClick={() => moveRule(i, 1)} disabled={i === rules.length - 1}><ChevronDown size={11} /></button>
              </div>
              <div style={{ fontWeight: 700, fontSize: '.8rem', color: 'var(--text-muted)', width: 18, paddingBottom: 8 }}>{i + 1}</div>

              <FIELD label="Service">
                <select className="form-control" style={{ width: 130 }} value={r.service || 'any'}
                  onChange={e => setRule(i, 'service', e.target.value)}>
                  {Object.entries(SERVICE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </FIELD>
              <FIELD label="Purpose">
                <select className="form-control" style={{ width: 110 }} value={r.purpose || 'any'}
                  onChange={e => setRule(i, 'purpose', e.target.value)}>
                  {Object.entries(PURPOSE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </FIELD>
              <FIELD label="Min ₹" hint="blank = no lower bound">
                <input type="number" min="0" step="0.01" className="form-control" style={{ width: 110 }}
                  value={r.minAmount ?? ''} onChange={e => setRule(i, 'minAmount', e.target.value === '' ? null : Number(e.target.value))} />
              </FIELD>
              <FIELD label="Max ₹" hint="blank = no upper bound">
                <input type="number" min="0" step="0.01" className="form-control" style={{ width: 110 }}
                  value={r.maxAmount ?? ''} onChange={e => setRule(i, 'maxAmount', e.target.value === '' ? null : Number(e.target.value))} />
              </FIELD>
              <FIELD label="→ Profile">
                <select className="form-control" style={{ width: 190 }} value={r.profile || ''}
                  onChange={e => setRule(i, 'profile', e.target.value)}>
                  {profiles.map(p => <option key={p._id} value={p._id}>{p.name}{p.isEnabled ? '' : ' (disabled)'}</option>)}
                </select>
              </FIELD>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', paddingBottom: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={r.isEnabled !== false}
                  onChange={e => setRule(i, 'isEnabled', e.target.checked)} />On
              </label>
              <button className="btn btn-danger btn-sm" style={{ marginBottom: 8 }}
                onClick={() => setRules(rs => rs.filter((_, j) => j !== i))}><Trash2 size={12} /></button>

              <div style={{ flexBasis: '100%', fontSize: '.78rem', color: 'var(--text-muted)' }}>
                {SERVICE_LABEL[r.service || 'any']} · {PURPOSE_LABEL[r.purpose || 'any']} · {amountLabel(r)}
                {' → '}<strong>{profiles.find(p => String(p._id) === String(r.profile))?.name || '(none)'}</strong>
              </div>
            </div>
          ))}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button className="btn btn-primary" onClick={saveRules} disabled={savingRules}>
              <Save size={14} style={{ marginRight: 6 }} />{savingRules ? 'Saving…' : 'Save Routing Rules'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Preview ── runs the real gatewayRouter server-side, so it can't drift from live behaviour */}
      <div className="card">
        <div className="card-body">
          <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Test Routing</h3>
          <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', margin: '0 0 14px' }}>
            Checks which profile a payment would actually go through. Uses the saved rules, so save first.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <FIELD label="Service">
              <select className="form-control" style={{ width: 140 }} value={preview.service}
                onChange={e => setPreview(p => ({ ...p, service: e.target.value, result: null }))}>
                <option value="catv">CableTV</option>
                <option value="broadband">Broadband</option>
              </select>
            </FIELD>
            <FIELD label="Purpose">
              <select className="form-control" style={{ width: 130 }} value={preview.purpose}
                onChange={e => setPreview(p => ({ ...p, purpose: e.target.value, result: null }))}>
                <option value="balance">Balance</option>
                <option value="renewal">Renewal</option>
              </select>
            </FIELD>
            <FIELD label="Amount ₹">
              <input type="number" min="0" className="form-control" style={{ width: 130 }} value={preview.amount}
                onChange={e => setPreview(p => ({ ...p, amount: e.target.value, result: null }))} />
            </FIELD>
            <button className="btn btn-secondary" style={{ marginBottom: 16 }} onClick={runPreview}>Check</button>
          </div>
          {preview.result && (
            <div style={{ fontSize: '.88rem', marginTop: 4 }}>
              → <strong>{preview.result.name}</strong> ({preview.result.provider || 'not configured'})
              {!preview.result.isEnabled && <span style={{ color: 'var(--danger)' }}> — disabled, this payment would be refused</span>}
              {preview.result.source?.startsWith('legacy') && (
                <span style={{ color: 'var(--text-muted)' }}> — no rule matched, fell back to the legacy per-service config</span>
              )}
              {preview.result.source?.startsWith('service-profile') && (
                <span style={{ color: 'var(--text-muted)' }}> — {SERVICE_LABEL[preview.service]} is pinned to this profile, so no rule was consulted</span>
              )}
              {preview.result.source?.startsWith('split') && (
                <span style={{ color: 'var(--text-muted)' }}> — picked by {SERVICE_LABEL[preview.service]}'s amount split, so no rule was consulted</span>
              )}
              {preview.result.source?.startsWith('custom') && (
                <span style={{ color: 'var(--text-muted)' }}> — {SERVICE_LABEL[preview.service]} is set to Custom on its Online PG tab, so no rule was consulted</span>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
